import { useTranslation } from 'react-i18next';
import { SubscriptionUsageMeter } from 'features/subscription/services/subscription-service';
import { formatWindow } from 'features/usage/utils/usage-format';
import { Card, CardContent } from 'components/ui/card';
import { cn } from 'lib/utils';
import { UsageSectionLabel } from './usage-section-label';
import { UsageStatCard } from './usage-stat-card';

// The section reports AI credits only; capacity meters such as knowledge folders and
// library storage belong to the plan, not to this period's consumption.
const METER_LABEL_KEYS: Record<string, string> = {
  'ai-credits': 'AI_CREDITS',
};

function formatCreditValue(value: number, _scale?: number) {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value);
  return (rounded === 0 ? 0 : rounded).toLocaleString();
}

function MeterBlock({ meter }: { meter: SubscriptionUsageMeter }) {
  const { t } = useTranslation();

  const title = t(METER_LABEL_KEYS[meter.meterKey], { defaultValue: 'AI Credits' });
  const hasOverage = meter.overage > 0;
  const usedPercent =
    meter.included > 0 ? Math.min(100, Math.round((meter.used / meter.included) * 100)) : 0;

  return (
    <div className="space-y-3">
      <Card className="border-border/70 shadow-none">
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm font-medium text-foreground">{title}</span>
            <span className="text-xs text-muted-foreground">
              {formatWindow(meter.periodStartUtc, meter.periodEndUtc)}
            </span>
          </div>

          <div className="space-y-1.5">
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  'h-full rounded-full',
                  hasOverage ? 'bg-warning-high-emphasis' : 'bg-primary'
                )}
                style={{ width: `${Math.max(usedPercent, meter.used > 0 ? 2 : 0)}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {t('CREDITS_USED_OF_INCLUDED', {
                  used: formatCreditValue(meter.used, meter.quantityScale),
                  included: formatCreditValue(meter.included, meter.quantityScale),
                  unit: meter.unitLabel,
                  defaultValue: '{{used}} of {{included}} {{unit}} used',
                })}
              </span>
              <span className="font-mono">{usedPercent}%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <UsageStatCard
          label={t('INCLUDED', { defaultValue: 'Included' })}
          value={formatCreditValue(meter.included, meter.quantityScale)}
          subLabel={t('PER_BILLING_PERIOD', { defaultValue: 'Per billing period' })}
        />
        <UsageStatCard
          label={t('USED', { defaultValue: 'Used' })}
          value={formatCreditValue(meter.used, meter.quantityScale)}
          subLabel={t('CREDITS_PROJECTED', {
            value: formatCreditValue(meter.projection, meter.quantityScale),
            defaultValue: 'Projected: {{value}}',
          })}
        />
        <UsageStatCard
          label={t('REMAINING', { defaultValue: 'Remaining' })}
          value={formatCreditValue(meter.remaining, meter.quantityScale)}
          subLabel={
            meter.allowed
              ? t('WITHIN_ALLOWANCE', { defaultValue: 'Within allowance' })
              : t('ALLOWANCE_EXCEEDED', { defaultValue: 'Allowance exceeded' })
          }
          valueClassName={meter.remaining > 0 ? 'text-success-green' : 'text-warning-high-emphasis'}
        />
        <UsageStatCard
          label={t('OVERAGE', { defaultValue: 'Overage' })}
          value={formatCreditValue(meter.overage, meter.quantityScale)}
          subLabel={t('BEYOND_INCLUDED', { defaultValue: 'Beyond included' })}
          valueClassName={hasOverage ? 'text-warning-high-emphasis' : undefined}
        />
      </div> */}
    </div>
  );
}

export function UsageCredits({
  meters,
  userId,
}: {
  meters: SubscriptionUsageMeter[];
  userId: string;
}) {
  const { t } = useTranslation();

  // The endpoint returns the same meter once per scope -- the organisation's under an
  // empty userId, then one row per user -- so the scope, not the key, is what tells the
  // rows apart. Showing the wrong one would report the whole org's spend as a person's.
  const creditMeters = meters.filter(
    (meter) => meter.meterKey in METER_LABEL_KEYS && meter.userId === userId
  );

  if (!creditMeters.length) return null;

  return (
    <div className="space-y-4">
      <UsageSectionLabel>
        {userId
          ? t('AI_CREDITS_PERSONAL', { defaultValue: 'AI credits (this user)' })
          : t('AI_CREDITS_ORG_WIDE', { defaultValue: 'AI credits (organization-wide)' })}
      </UsageSectionLabel>
      <div className="space-y-6">
        {creditMeters.map((meter) => (
          <MeterBlock key={`${meter.meterKey}:${meter.userId}`} meter={meter} />
        ))}
      </div>
    </div>
  );
}
